import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../context/ToastContext';
import { sendPlatformNotification } from '../../lib/adminApi';

export default function NotifyUsersPage() {
  const { profile } = useAdminAuth();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title?.trim() || !body?.trim()) {
      toast.error('Please enter both title and message.');
      return;
    }
    setSubmitting(true);
    try {
      await sendPlatformNotification({
        title: title.trim(),
        body: body.trim(),
        recipientType,
        adminUserId: profile?.user_id,
      });
      toast.success(
        recipientType === 'all'
          ? 'Notification sent to all users.'
          : recipientType === 'customers'
            ? 'Notification sent to customers.'
            : 'Notification sent to caterers.'
      );
      setTitle('');
      setBody('');
    } catch (err) {
      toast.error(err?.message ?? 'Failed to send notification.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold text-foreground">Notify Users</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Send a notification to customers and/or caterers. It appears in their app notification panel and can trigger a push notification if enabled.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm">
        <div>
          <label htmlFor="recipientType" className="block text-sm font-medium text-foreground mb-2">
            Send to
          </label>
          <select
            id="recipientType"
            value={recipientType}
            onChange={(e) => setRecipientType(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All users (customers + caterers)</option>
            <option value="customers">Customers only</option>
            <option value="caterers">Caterers only</option>
          </select>
        </div>

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-foreground mb-2">
            Title <span className="text-error">*</span>
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. Platform maintenance tonight"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
          <p className="mt-1 text-xs text-muted-foreground">{title.length}/200</p>
        </div>

        <div>
          <label htmlFor="body" className="block text-sm font-medium text-foreground mb-2">
            Message <span className="text-error">*</span>
          </label>
          <textarea
            id="body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Enter your notification message..."
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
          <p className="mt-1 text-xs text-muted-foreground">{body.length}/2000</p>
        </div>

        <button
          type="submit"
          disabled={submitting || !title?.trim() || !body?.trim()}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon name="Send" size={16} />
          {submitting ? 'Sending...' : 'Send notification'}
        </button>
      </form>
    </div>
  );
}
